import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "admin-lte/dist/css/adminlte.min.css";
import DataTable from "react-data-table-component";
import Select from "react-select";
import apiManager from "../../../api/apiManager";
import API_ENDPOINTS from "../../../api/apiEndpoints";
import {
  showLoader,
  hideLoader,
} from "../../../shared/Services/Loader/loaderService";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/Services/toasterService";
import { useLocalization } from "../../../context/LocalizationContext";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../../shared/helpers/formUtils";

const ServiceDeskUsers = () => {
  const { t: localization } = useLocalization();
  const navigate = useNavigate();

  const [userList, setUserList] = useState([]);
  const [gridSearch, setGridSearch] = useState("");
  const [dealerList, setDealerList] = useState([]);

  // Assign shop modal (kept for SSO users if needed in future)
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // View details modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  // Resend invitation modal
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendUser, setResendUser] = useState(null);
  const [isResending, setIsResending] = useState(false);

  // Action dropdown
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownRow(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchAllUsers();
    fetchDealerList();
  }, []);

  const fetchAllUsers = async () => {
    try {
      showLoader();

      // Fetch SSO (Microsoft) users
      let ssoUsers = [];
      try {
        const ssoResponse = await apiManager.get(
          API_ENDPOINTS.DEALER_TENANT_USER,
          {},
        );
        if (ssoResponse.status === 200) {
          ssoUsers = (ssoResponse.data || []).map((u) => ({
            ...u,
            userType: "sso",
          }));
        }
      } catch (_) {}

      // Fetch External users
      let externalUsers = [];
      try {
        const extResponse = await apiManager.get(
          API_ENDPOINTS.EXTERNAL_USER_GET_ALL,
          {},
        );
        if (extResponse.status === 200) {
          externalUsers = (extResponse.data || []).map((u) => ({
            ...u,
            userType: "external",
          }));
        }
      } catch (_) {}

      setUserList([...ssoUsers, ...externalUsers]);
    } catch (e) {
      showErrorMessage(e.message);
    } finally {
      hideLoader();
    }
  };

  const getDealerDisplayName = (user) => {
    const directDealerName =
      user?.dealerName ||
      user?.assignedDealerName ||
      user?.dealer?.dealerName ||
      user?.dealer?.name ||
      user?.dealer?.displayName;

    if (directDealerName) return directDealerName;

    const assignedDealer = user?.assignedDealer;
    if (!assignedDealer) return "-";

    const matchedDealer = dealerList.find(
      (dealer) =>
        String(dealer.value) === String(assignedDealer) ||
        dealer.label === assignedDealer,
    );
    return matchedDealer?.label || assignedDealer;
  };

  const fetchDealerList = async () => {
    try {
      const response = await apiManager.get(API_ENDPOINTS.DEALER_GET_LIST);
      if (response.status === 200) {
        setDealerList(
          (response.data || []).map((d) => ({
            value: d.dealerId || d.id,
            label: d.dealerName || d.name || d.displayName,
          })),
        );
      }
    } catch (e) {
      showErrorMessage(e.message);
    }
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);

    // preload dealers if not loaded
    if (dealerList.length === 0) {
      fetchDealerList();
    }

    // preselect dealer if already assigned
    if (user.assignedDealer) {
      const existingDealer = dealerList.find(
        (d) =>
          String(d.value) === String(user.assignedDealer) ||
          d.label === user.assignedDealer ||
          d.label === user.assignedDealerName,
      );
      setSelectedDealer(existingDealer || null);
    } else {
      setSelectedDealer(null);
    }
    setShowAssignModal(true);
  };

  const isSameDealerAsAssigned = (user, dealerOption) => {
    if (!user || !dealerOption) {
      return false;
    }

    const assignedDealerId = user.assignedDealer;
    const assignedDealerName =
      user.assignedDealerName ||
      user.dealerName ||
      user.dealer?.dealerName ||
      user.dealer?.name ||
      user.dealer?.displayName;

    const sameById =
      assignedDealerId !== undefined &&
      assignedDealerId !== null &&
      String(dealerOption.value) === String(assignedDealerId);

    const sameByName =
      !!assignedDealerName && dealerOption.label === assignedDealerName;

    return sameById || sameByName;
  };

  const canAssignDealer =
    !!selectedDealer &&
    !!selectedUser &&
    !isSameDealerAsAssigned(selectedUser, selectedDealer);

  const handleAssignDealer = async () => {
    if (!selectedDealer) {
      showErrorMessage("Please select a dealer.");
      return;
    }

    if (!canAssignDealer) {
      return;
    }

    try {
      setIsAssigning(true);
      showLoader();
      const response = await apiManager.post(API_ENDPOINTS.DEALER_ASSIGN_USER, {
        userId: selectedUser.id || selectedUser.userId,
        dealerId: selectedDealer.value,
      });
      if (response.status === 200) {
        showSuccessMessage("Shop assigned successfully.");
        setShowAssignModal(false);
        setSelectedUser(null);
        setSelectedDealer(null);
        fetchAllUsers();
      } else {
        showErrorMessage("Failed to assign dealer.");
      }
    } catch (e) {
      showErrorMessage(e.message);
    } finally {
      setIsAssigning(false);
      hideLoader();
    }
  };

  // Delete external user
  const requestDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      showLoader();
      const response = await apiManager.delete(
        `${API_ENDPOINTS.EXTERNAL_USER_DELETE}/${userToDelete.id}`,
      );
      if (response.status === 200) {
        showSuccessMessage("User deleted successfully.");
        setShowDeleteModal(false);
        setUserToDelete(null);
        fetchAllUsers();
      } else {
        showErrorMessage("Failed to delete user.");
      }
    } catch (e) {
      showErrorMessage(e);
    } finally {
      hideLoader();
    }
  };

  // Toggle Active / Inactive external user
  const handleToggleStatus = async (user) => {
    try {
      showLoader();
      const response = await apiManager.put(
        API_ENDPOINTS.EXTERNAL_USER_TOGGLE_STATUS,
        { userId: user.id, isActive: !user.isActive },
      );
      if (response.status === 200) {
        showSuccessMessage(
          `User ${!user.isActive ? "activated" : "deactivated"} successfully.`,
        );
        setUserList((prev) =>
          prev.map((u) =>
            u.id === user.id ? { ...u, isActive: !user.isActive } : u,
          ),
        );
      } else {
        showErrorMessage("Failed to update user status.");
      }
    } catch (e) {
      showErrorMessage(e);
    } finally {
      hideLoader();
    }
  };

  // View user details
  const openViewModal = (user) => {
    setViewUser(user);
    setShowViewModal(true);
  };

  // Resend invitation
  const requestResendInvitation = (user) => {
    setResendUser(user);
    setShowResendModal(true);
  };

  const confirmResendInvitation = async () => {
    if (!resendUser) return;
    setIsResending(true);
    try {
      showLoader();
      const response = await apiManager.post(
        API_ENDPOINTS.EXTERNAL_USER_RESEND_INVITATION,
        { userId: resendUser.id },
      );
      if (response.status === 200) {
        showSuccessMessage(
          `Invitation resent to ${resendUser.email || resendUser.displayName}.`,
        );
        setShowResendModal(false);
        setResendUser(null);
        fetchAllUsers(); // refresh so invitation status updates
      } else {
        showErrorMessage("Failed to resend invitation. Please try again.");
      }
    } catch (e) {
      showErrorMessage(e || "Failed to resend invitation.");
    } finally {
      setIsResending(false);
      hideLoader();
    }
  };

  const columns = [
    {
      name: localization("name"),
      selector: (row) => row.displayName || localization("not_available"),
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: "Email",
      selector: (row) => row.email || row.userName || "-",
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: localization("assigned_shop"),
      selector: (row) => getDealerDisplayName(row),
      sortable: true,
      wrap: true,
      grow: 1,
    },
    {
      name: "User Type",
      cell: (row) =>
        row.userType === "sso" ? (
          <span className="badge bg-primary">Microsoft</span>
        ) : (
          <span className="badge bg-success">External</span>
        ),
      sortable: false,
      width: "120px",
    },
    {
      name: (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {localization("actions")}
        </div>
      ),
      cell: (row) => {
        const isSSO = row.userType === "sso";
        const isOpen = openDropdownRow === row.id;

        const actions = [
          {
            label: "Assign Shop",
            icon: "fas fa-link",
            color: "#b8860b",
            bgColor: "rgba(184,134,11,0.12)",
            onClick: () => {
              openAssignModal(row);
              setOpenDropdownRow(null);
            },
            disabled: false,
          },
          {
            label: "View Details",
            icon: "fas fa-eye",
            color: "#1a73e8",
            bgColor: "rgba(26,115,232,0.12)",
            onClick: () => {
              openViewModal(row);
              setOpenDropdownRow(null);
            },
            disabled: false,
          },
          {
            label: "Edit User",
            icon: "fas fa-edit",
            color: "#b8860b",
            bgColor: "rgba(184,134,11,0.12)",
            onClick: () => {
              if (!isSSO) {
                navigate(`/servicedeskusers/edit/${row.id}`);
                setOpenDropdownRow(null);
              }
            },
            disabled: isSSO,
            title: isSSO ? "Cannot edit Microsoft SSO users" : undefined,
          },
          {
            label: row.isActive !== false ? "Deactivate" : "Activate",
            icon: `fas ${row.isActive !== false ? "fa-ban" : "fa-check"}`,
            color: row.isActive !== false ? "#e67e22" : "#27ae60",
            bgColor:
              row.isActive !== false
                ? "rgba(230,126,34,0.12)"
                : "rgba(39,174,96,0.12)",
            onClick: () => {
              if (!isSSO) {
                handleToggleStatus(row);
                setOpenDropdownRow(null);
              }
            },
            disabled: isSSO,
            title: isSSO
              ? "Cannot change Microsoft SSO user status"
              : undefined,
          },
          {
            label: "Resend Invitation",
            icon: "fas fa-paper-plane",
            color: "#17a2b8",
            bgColor: "rgba(23,162,184,0.12)",
            onClick: () => {
              requestResendInvitation(row);
              setOpenDropdownRow(null);
            },
            disabled: row?.isPasswordSet || isSSO,
            title: isSSO
              ? "Microsoft SSO users do not require an invitation"
              : row?.isPasswordSet
                ? "Password has already been set by the user"
                : undefined,
          },
          {
            label: "Delete User",
            icon: "fas fa-trash",
            color: "#e74c3c",
            bgColor: "rgba(231,76,60,0.12)",
            onClick: () => {
              if (!isSSO) {
                requestDelete(row);
                setOpenDropdownRow(null);
              }
            },
            disabled: isSSO,
            title: isSSO ? "Cannot delete Microsoft SSO users" : undefined,
          },
        ];

        return (
          <div>
            <button
              className="action-gear-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (!isOpen) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setDropdownPos({
                    top: rect.top + rect.height / 2,
                    right: window.innerWidth - rect.left + 8,
                  });
                }
                setOpenDropdownRow(isOpen ? null : row.id);
              }}
              title="Actions"
            >
              <i
                className={`fas fa-cog action-gear-icon${isOpen ? " active" : ""}`}
              />
            </button>

            {isOpen &&
              ReactDOM.createPortal(
                <div
                  className="action-dropdown-menu"
                  style={{
                    position: "fixed",
                    top: dropdownPos.top,
                    right: dropdownPos.right,
                    transform: "translateY(-50%)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {actions.map((action, idx) => (
                    <button
                      key={idx}
                      className={`action-dropdown-item${action.disabled ? " disabled" : ""}`}
                      onClick={action.disabled ? undefined : action.onClick}
                      disabled={action.disabled}
                      title={action.title}
                    >
                      <span
                        className="action-item-icon"
                        style={{
                          color: action.color,
                          backgroundColor: action.bgColor,
                        }}
                      >
                        <i className={action.icon} />
                      </span>
                      <span className="action-item-label">{action.label}</span>
                    </button>
                  ))}
                </div>,
                document.body,
              )}
          </div>
        );
      },
      ignoreRowClick: true,
      allowOverflow: true,
      width: "70px",
      style: { justifyContent: "center" },
    },
  ];

  const customStyles = {
    table: { style: { minWidth: "900px" } },
    headCells: {
      style: {
        backgroundColor: "#f4f4f4",
        color: "#333",
        borderRight: "1px solid #ccc",
        fontSize: "16px",
        fontWeight: 600,
        whiteSpace: "nowrap",
      },
    },
    cells: {
      style: {
        borderRight: "1px solid #eee",
        fontSize: "13px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: "300px",
      },
    },
    rows: { style: { minHeight: "50px" } },
  };

  const filteredData = userList.filter((user) => {
    const keyword = gridSearch?.toLowerCase();
    const dealerName = getDealerDisplayName(user)?.toLowerCase();
    const email = (user.email || user.userName || "").toLowerCase();
    return (
      (user.displayName || "")?.toLowerCase().includes(keyword) ||
      dealerName.includes(keyword) ||
      email.includes(keyword)
    );
  });

  return (
    <section className="container-fluid content-wrapper select-vehicle-content">
      <div className="content form-card">
        <div className="page-title">
          <div className="container-fluid p-0">
            <div className="row">
              <div className="col-md-6 d-flex align-items-center">
                <h1>{localization("available_service_desk_users")}</h1>
              </div>
              <div className="col-md-6 d-flex justify-content-end">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="d-flex justify-content-end"
                    style={{ position: "relative", width: "250px" }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder={localization("search")}
                      style={{ width: "100%", paddingRight: "2.5rem" }}
                      value={gridSearch}
                      onChange={(e) => setGridSearch(e.target.value)}
                      id="user-grid-search"
                      aria-label={localization("search_placeholder")}
                    />
                    {gridSearch && (
                      <button
                        type="button"
                        className="btn btn-link p-0 position-absolute"
                        style={{
                          right: "8px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#888",
                          fontSize: "1.2rem",
                          textDecoration: "none",
                        }}
                        onClick={() => {
                          setGridSearch("");
                          setTimeout(() => {
                            document
                              .getElementById("user-grid-search")
                              ?.focus();
                          }, 0);
                        }}
                        tabIndex={-1}
                        aria-label={localization("clear_search")}
                      >
                        <i className="fas fa-times-circle"></i>
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => navigate("/servicedeskusers/create")}
                  >
                    <i className="fas fa-user-plus me-1"></i> Add User
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid p-0">
          <div className="row justify-content-center mt-3">
            <div className="col-lg-12">
              <div className="card-body p-0">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination={userList.length > 0}
                  striped
                  highlightOnHover
                  pointerOnHover
                  noDataComponent={localization("no_record_found")}
                  responsive
                  customStyles={customStyles}
                  noHeader
                />
                <div className="mb-3">
                  <button
                    className="btn btn-blue btn-sm"
                    onClick={() => navigate("/admin-dashboard")}
                  >
                    {localization("back")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Assign Shop Modal ── */}
      {showAssignModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Assign Shop</h5>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Assigning Shop For:{" "}
                  <strong>{selectedUser?.displayName}</strong>
                </p>
                <Select
                  options={dealerList}
                  value={selectedDealer}
                  onChange={(option) => setSelectedDealer(option)}
                  placeholder="Select a dealer..."
                  classNamePrefix="select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#ced4da",
                      "&:hover": { borderColor: "#80bdff" },
                    }),
                  }}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={handleAssignDealer}
                  disabled={isAssigning || !canAssignDealer}
                >
                  {isAssigning ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Assigning...
                    </>
                  ) : (
                    "Assign"
                  )}
                </button>
                <button
                  className="btn btn-cancel"
                  onClick={() => {
                    setShowAssignModal(false);
                    setSelectedUser(null);
                    setSelectedDealer(null);
                  }}
                >
                  {localization("cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Delete User</h5>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{userToDelete?.displayName}</strong>? This action
                  cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-red btn-sm" onClick={confirmDelete}>
                  Delete
                </button>
                <button
                  className="btn btn-cancel"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                >
                  {localization("cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── View Details Modal ── */}
      {showViewModal && viewUser && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">User Details</h5>
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <th>{localization("name")}</th>
                      <td>{viewUser.displayName || "-"}</td>
                    </tr>
                    <tr>
                      <th>Email</th>
                      <td>{viewUser.email || viewUser.userName || "-"}</td>
                    </tr>
                    <tr>
                      <th>User Type</th>
                      <td>
                        {viewUser.userType === "sso" ? (
                          <span className="badge bg-primary">
                            Microsoft SSO
                          </span>
                        ) : (
                          <span className="badge bg-success">External</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>{localization("assigned_shop")}</th>
                      <td>{getDealerDisplayName(viewUser)}</td>
                    </tr>
                    {viewUser.userType === "external" && (
                      <>
                        <tr>
                          <th>Phone</th>
                          <td> {viewUser ? formatPhoneNumber(viewUser.phoneNumber) : "-"}</td>
                        </tr>
                        <tr>
                          <th>Address</th>
                          <td>{viewUser.address || "-"}</td>
                        </tr>
                        <tr>
                          <th>Currency</th>
                          <td>{viewUser.currency || "-"}</td>
                        </tr>
                        <tr>
                          <th>Status</th>
                          <td>
                            {viewUser.isActive !== false ? (
                              <span className="badge bg-success">Active</span>
                            ) : (
                              <span className="badge bg-danger">Inactive</span>
                            )}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-blue btn-sm"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewUser(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Resend Invitation Confirmation Modal ── */}
      {showResendModal && resendUser && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-paper-plane me-2 text-teal" />
                  Resend Invitation
                </h5>
              </div>
              <div className="modal-body">
                <p>
                  Resend the invitation email to{" "}
                  <strong>{resendUser.displayName}</strong>?
                </p>
                <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                  <i className="fas fa-info-circle me-1" />
                  Any previously sent invitation link will be invalidated. The
                  user will receive a new link valid for{" "}
                  <strong>24 hours</strong>.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-teal btn-sm text-white"
                  onClick={confirmResendInvitation}
                  disabled={isResending}
                >
                  {isResending ? (
                    <>Sending…</>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane me-1" />
                      Send Invitation
                    </>
                  )}
                </button>
                <button
                  className="btn btn-cancel"
                  onClick={() => {
                    setShowResendModal(false);
                    setResendUser(null);
                  }}
                  disabled={isResending}
                >
                  {localization("cancel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ServiceDeskUsers;
